import {
    completeIssueReportingChat,
    IssueReportingChatMessage,
    IssueReportingResponseChunk, UserMessage, UserPhoto, UserYesNoResponse
} from "../api/issueReporting.ts";
import {useCallback, useRef, useState} from "react";
import {uploadFile} from "../api/file.ts";

export interface IssueReportingChatHook {
    reset: () => void,
    messages: IssueReportingChatMessage[],
    sendMessage: (text: string) => void,
    sendPhoto: (file: File) => void,
    sendYesNoResponse: (response: 'yes' | 'no') => void,
    disableInput: boolean,
    waitingForResponse: boolean
}

const randomId = () => {
    const random = Math.random().toString(16);
    return random.slice(2, 10);
}

const concatenateMessages = (existingMessage: IssueReportingChatMessage, newMessage: IssueReportingResponseChunk): IssueReportingChatMessage => {
    if (existingMessage.type !== newMessage.type) {
        console.error("Attempted to concatenate messages of different types", existingMessage, newMessage);
        return newMessage;
    }

    if (existingMessage.type === 'assistantMessage' && newMessage.type === 'assistantMessage') {
        return {
            ...existingMessage,
            content: existingMessage.content + newMessage.content
        }
    }

    if (existingMessage.type === 'assistantIssueCreated' && newMessage.type === 'assistantIssueCreated') {
        return newMessage
    }

    if (existingMessage.type === 'assistantYesNoQuestion' && newMessage.type === 'assistantYesNoQuestion') {
        return newMessage
    }

    return newMessage
};

export const useIssueReportingChat = (siteId: string, language: string): IssueReportingChatHook => {
    const [messages, setMessages] = useState<IssueReportingChatMessage[]>([]);
    const [disableInput, setDisableInput] = useState(false);
    const [waitingForResponse, setWaitingForResponse] = useState(false);

    // Track a generation number to invalidate stale updates
    const generationRef = useRef(0);

    const reset = useCallback(() => {
        generationRef.current += 1;
        setMessages([]);
        setDisableInput(false);
        setWaitingForResponse(false);
    }, []);

    const onChunk = useCallback((chunk: IssueReportingResponseChunk, generation: number) => {
        console.log('[FRONTEND] Received chunk:', chunk);
        if (generationRef.current !== generation) return;

        setWaitingForResponse(false);
        setMessages(currentMessages => {
            const existingMessage = currentMessages.find(message => message.id === chunk.id);
            if (existingMessage) {
                const newMessage = concatenateMessages(existingMessage, chunk);
                return currentMessages.map(message => message.id === existingMessage.id ? newMessage : message);
            } else {
                return [...currentMessages, chunk];
            }
        });
    }, []);

    const getNextAssistantMessage = useCallback(async (messages: IssueReportingChatMessage[]) => {
        const currentGeneration = generationRef.current;

        setDisableInput(true);
        setWaitingForResponse(true);

        await completeIssueReportingChat(siteId, { messages, language }, (chunk) => {
            onChunk(chunk, currentGeneration);
        });

        if (generationRef.current === currentGeneration) {
            setDisableInput(false);
        }
    }, [siteId, language, onChunk]);

    const sendMessage = useCallback((text: string) => {
        const newMessage: UserMessage = { id: randomId(), type: 'userMessage', content: text };
        const newMessages = [...messages, newMessage];
        setMessages(newMessages);
        getNextAssistantMessage(newMessages).catch(e => console.error("Error sending message", e));
    }, [messages, getNextAssistantMessage]);

    const sendYesNoResponse = useCallback((response: 'yes' | 'no') => {
        const newMessage: UserYesNoResponse = { id: randomId(), type: 'userYesNoResponse', response };
        const newMessages = [...messages, newMessage];
        setMessages(newMessages);
        getNextAssistantMessage(newMessages).catch(e => console.error("Error sending yes/no response", e));
    }, [messages, getNextAssistantMessage]);

    const sendPhoto = useCallback(async (file: File) => {
        const src = URL.createObjectURL(file);

        const messageId = randomId();

        const newMessage: UserPhoto = { id: messageId, type: 'userPhoto', photoId: messageId, fileKey: '', src }
        const newMessages = [...messages, newMessage]
        setMessages(newMessages);

        const fileKey = await uploadFile('issue-reporting-images', file);
        const newMessagesWithKey = newMessages.map(message => message.id === messageId ? { ...message, fileKey } : message)
        setMessages(newMessagesWithKey);

        getNextAssistantMessage(newMessagesWithKey).catch(e => console.error("Error sending message", e));
    }, [messages]);

    return { reset, messages, sendMessage, sendPhoto, sendYesNoResponse, disableInput, waitingForResponse };
};
